import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AdminMe, OrganizationStatus } from "@vital/shared";
import { organizationAllowsAdminWrites } from "../lib/tenant-ui";
import { api, clearCsrfCache } from "../lib/api";

type AuthState = {
  user: AdminMe | null;
  loading: boolean;
  switchingOrganization: boolean;
  /** Bumps on every successful org switch so mounted pages remount even if id repeats. */
  tenantEpoch: number;
  refresh: () => Promise<void>;
  setUser: (user: AdminMe | null) => void;
  switchOrganization: (organizationId: string) => Promise<AdminMe>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [switchingOrganization, setSwitchingOrganization] = useState(false);
  const [tenantEpoch, setTenantEpoch] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const me = await api.getMe();
      setUser(me);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const switchOrganization = useCallback(async (organizationId: string) => {
    setSwitchingOrganization(true);
    try {
      const me = await api.selectOrganization(organizationId);
      setUser(me);
      setTenantEpoch((n) => n + 1);
      return me;
    } finally {
      setSwitchingOrganization(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      clearCsrfCache();
      setUser(null);
      setTenantEpoch(0);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      switchingOrganization,
      tenantEpoch,
      refresh,
      setUser,
      switchOrganization,
      logout,
    }),
    [user, loading, switchingOrganization, tenantEpoch, refresh, switchOrganization, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useActiveOrganization(): {
  organizationId: string | null;
  displayName: string | null;
  code: string | null;
  status: OrganizationStatus | null;
  writable: boolean;
  needsSelection: boolean;
  canSwitch: boolean;
  hasNoOrganizations: boolean;
} {
  const { user } = useAuth();
  const selectable = user?.selectableOrganizations ?? [];
  const selectedId = user?.selectedOrganizationId ?? null;
  const active = selectable.find((o) => o.organizationId === selectedId) ?? null;
  const status = (user?.organizationStatus as OrganizationStatus | null | undefined) ?? active?.status ?? null;

  return {
    organizationId: selectedId,
    displayName: active?.displayName ?? null,
    code: active?.code ?? null,
    status,
    writable: organizationAllowsAdminWrites(status),
    needsSelection: !selectedId && selectable.length > 1,
    canSwitch: selectable.length > 1,
    hasNoOrganizations: selectable.length === 0,
  };
}
